import { useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 10_000; // 10s

/**
 * Polls /api/arena/feed for new close events and pushes them to the global
 * ArenaTakeover queue. Respects the current user's DND setting.
 */
export function useArenaFeed() {
  const { user } = useAuth();
  const sinceRef = useRef(null);
  const dndRef = useRef(false);

  useEffect(() => {
    if (!user || user.must_change_password) return;
    if (!["admin", "rep", "office_manager"].includes(user.role)) return;

    let cancelled = false;

    // Seed since so we don't replay old events on load.
    (async () => {
      try {
        const { data } = await api.get("/arena/feed", { params: { limit: 1 } });
        sinceRef.current = data?.server_time || new Date().toISOString();
      } catch (e) {
        sinceRef.current = new Date().toISOString();
      }
      try {
        const { data: me } = await api.get("/arena/me");
        dndRef.current = !!me?.dnd;
      } catch (e) {
        dndRef.current = false;
      }
    })();

    const tick = async () => {
      if (cancelled) return;
      if (!sinceRef.current) return;
      try {
        const { data } = await api.get("/arena/feed", { params: { since: sinceRef.current } });
        const events = data?.events || [];
        if (events.length > 0) {
          for (const ev of events) {
            if (window.__mscTakeover?.push) {
              window.__mscTakeover.push({
                ...ev,
                dnd: dndRef.current,
                has_walkup: true, // let ArenaTakeover attempt fetch; 404 is silent
              });
            }
          }
          // Advance since to the last event's timestamp
          sinceRef.current = events[events.length - 1].created_at;
        }
        if (data?.server_time && (!sinceRef.current || data.server_time > sinceRef.current)) {
          // keep server_time as a floor when no events
          if (events.length === 0) sinceRef.current = data.server_time;
        }
      } catch (e) {
        // silent — 401/network hiccups
      }
    };

    const timer = setInterval(tick, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);
}
